import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Link, useParams } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import {
  getOpsAccount,
  getOpsAccountEvals,
  updateOpsAccount,
} from '../../../requests';
import { TOpsAccountUpdate } from '../../../types';
import AttachmentsPanel from '../AttachmentsPanel';
import CommentThread from '../CommentThread';
import { Card, PageBody, PageHeader, Pill } from '../ui';

const money = (v: number | null) =>
  v == null
    ? '—'
    : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const Panel: React.FC<{
  title: string;
  count?: number;
  children: React.ReactNode;
  right?: React.ReactNode;
}> = ({ title, count, children, right }) => (
  <Card title={count != null ? `${title} · ${count}` : title} right={right}>
    <div className="px-5 py-4">{children}</div>
  </Card>
);

const itemLinkClass =
  'block rounded-lg border border-white-three px-3 py-2 text-sm transition-colors hover:border-blue-500 hover:bg-blue-100/30';

const Empty = () => <div className="text-sm text-gray-two">none</div>;

const OpsAccountView: React.FC = () => {
  const [showClosed, setShowClosed] = React.useState(false);
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: a, isLoading } = useQuery({
    queryKey: ['opsAccount', id],
    queryFn: () => getOpsAccount(id!),
    enabled: Boolean(id),
  });

  // The only editable field on this screen today; the profile fields are
  // sent back unchanged so the PATCH stays a full account update.
  const quietMut = useMutation({
    mutationFn: (dto: TOpsAccountUpdate) => updateOpsAccount(id!, dto),
    onSuccess: (_, dto) => {
      showToast(
        dto.commsOptOut
          ? 'Quiet account on — automated learner mail is held for this account'
          : 'Quiet account off — automated learner mail resumes',
        'success',
      );
      queryClient.invalidateQueries({ queryKey: ['opsAccount', id] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const { data: evals } = useQuery({
    queryKey: ['opsAccountEvals', id],
    queryFn: () => getOpsAccountEvals(id!),
    enabled: Boolean(id),
  });

  if (isLoading || !a) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  const metaLine =
    [a.type, a.industry, a.tier ? `Tier: ${a.tier}` : null, a.phone]
      .filter(Boolean)
      .join(' · ') || 'no profile details';

  return (
    <>
      <PageHeader
        title={a.name ?? '—'}
        back={{ to: PATH.opsAccounts, label: 'All accounts' }}
        meta={
          <span className="flex flex-wrap items-center gap-2">
            <span>
              {metaLine}
              {a.website && <> · {a.website}</>}
              {a.accountOwner?.name && (
                <>
                  {' · owner '}
                  <span
                    title={`From the ${a.accountOwner.basis}${
                      a.accountOwner.asOf ? ` (${a.accountOwner.asOf})` : ''
                    }; HubSpot will own this after the CRM move`}
                  >
                    {a.accountOwner.name}
                  </span>
                </>
              )}
            </span>
            {a.commsOptOut && <Pill value="Quiet" tone="neutral" />}
          </span>
        }
        actions={
          <label className="flex items-center gap-2 text-sm text-gray-five">
            <input
              type="checkbox"
              className="ops-check h-4 w-4 accent-blue-primary"
              checked={a.commsOptOut}
              disabled={quietMut.isPending}
              onChange={(e) =>
                quietMut.mutate({
                  name: a.name ?? '',
                  type: a.type ?? undefined,
                  industry: a.industry ?? undefined,
                  phone: a.phone ?? undefined,
                  website: a.website ?? undefined,
                  commsOptOut: e.target.checked,
                })
              }
            />
            Quiet account — no automated learner mail
          </label>
        }
      />
      <PageBody wide>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Panel title="Programs" count={a.programs.length}>
            {a.programs.length === 0 ? (
              <Empty />
            ) : (
              <div className="grid gap-2">
                {a.programs.map((p) => (
                  <Link
                    key={p.id}
                    to={`${PATH.opsPrograms}?id=${p.id}`}
                    className={itemLinkClass}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-gray-four">
                        {p.name}
                      </span>
                      <Pill value={p.status} />
                    </div>
                    <div className="mt-0.5 text-xs text-gray-three">
                      {p.number ? (
                        <span className="font-mono">{p.number} · </span>
                      ) : null}
                      {p.startDate} → {p.endDate} · invested {money(p.invested)}{' '}
                      · remaining <b>{money(p.remaining)}</b> ·{' '}
                      {p.members ?? '—'} members
                      {p.contractPoc ? ` · POC ${p.contractPoc}` : ''}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Panel>

          <Panel
            title="Upcoming classes"
            count={a.classCounts?.upcoming ?? a.upcomingClasses.length}
            right={
              <span className="text-xs">
                <Link
                  className="text-blue-primary hover:underline"
                  to={`${PATH.opsClasses}?accountId=${
                    a.id
                  }&label=${encodeURIComponent(a.name ?? '')}`}
                >
                  all upcoming
                </Link>
                {' · '}
                <Link
                  className="text-blue-primary hover:underline"
                  to={`${PATH.opsClasses}?accountId=${
                    a.id
                  }&when=past&label=${encodeURIComponent(a.name ?? '')}`}
                >
                  {a.classCounts?.past ?? '—'} past
                </Link>
              </span>
            }
          >
            {a.upcomingClasses.length === 0 ? (
              <Empty />
            ) : (
              <div className="grid gap-2">
                {a.upcomingClasses.map((c) => (
                  <Link
                    key={c.id}
                    to={PATH.opsClassEdit.replace(':id', c.id)}
                    className={itemLinkClass}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-gray-four">
                        {c.name}
                      </span>
                      <span className="whitespace-nowrap text-xs text-gray-three">
                        {c.start?.slice(0, 10)}
                      </span>
                    </div>
                    <div className="mt-0.5 text-xs text-gray-three">
                      {[c.type, c.status, c.instructor]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Panel>

          <Panel
            title="Recent registrations"
            count={a.recentRegistrations.length}
          >
            {a.recentRegistrations.length === 0 ? (
              <Empty />
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {a.recentRegistrations.map((r) => (
                    <tr key={r.id} className="border-b border-white-three">
                      <td className="py-1.5 pr-2 font-medium text-gray-four">
                        {r.learner}
                      </td>
                      <td className="py-1.5 pr-2 text-gray-three">
                        {r.course}
                      </td>
                      <td className="whitespace-nowrap py-1.5 pr-2 text-xs text-gray-bold">
                        {r.start?.slice(0, 10)}
                      </td>
                      <td className="whitespace-nowrap py-1.5 text-xs">
                        <Pill value={r.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>

          <Panel
            title={showClosed ? 'Cases · open and closed' : 'Open cases'}
            count={
              showClosed
                ? a.openCases.length + (a.closedCases?.length ?? 0)
                : a.openCases.length
            }
            right={
              <span className="text-xs">
                <button
                  type="button"
                  className="text-blue-primary hover:underline"
                  onClick={() => setShowClosed((v) => !v)}
                >
                  {showClosed
                    ? 'hide closed'
                    : `show closed (${a.closedCases?.length ?? 0})`}
                </button>
                {' · '}
                <Link
                  className="text-blue-primary hover:underline"
                  to={`${PATH.opsCases}?new=1&accountId=${a.id}`}
                >
                  open a case
                </Link>
              </span>
            }
          >
            {a.openCases.length === 0 &&
            !(showClosed && a.closedCases?.length) ? (
              <Empty />
            ) : (
              <div className="grid gap-1">
                {[
                  ...a.openCases,
                  ...(showClosed ? a.closedCases ?? [] : []),
                ].map((c) => (
                  <Link
                    key={c.id}
                    to={`${PATH.opsCases}?id=${c.id}`}
                    className="text-sm text-gray-five transition-colors hover:text-blue-primary"
                  >
                    <span className="text-xs text-gray-bold">
                      {c.createdDate?.slice(0, 10)} · {c.status} ·{' '}
                    </span>
                    {c.subject ?? '(no subject)'}
                  </Link>
                ))}
              </div>
            )}
          </Panel>

          <Panel
            title="Evaluations"
            count={evals?.count}
            right={
              <Link
                className="text-xs text-blue-primary hover:underline"
                to={`${PATH.opsEvals}?accountId=${
                  a.id
                }&label=${encodeURIComponent(a.name ?? '')}`}
              >
                all evaluations
              </Link>
            }
          >
            {!evals || evals.count === 0 ? (
              <Empty />
            ) : (
              <div className="text-sm">
                <div className="pb-2 text-gray-five">
                  avg rating <b>{evals.avgRating ?? '—'}</b>/5 · recommend{' '}
                  <b>{evals.avgRecommend ?? '—'}</b>/10
                </div>
                <div className="grid gap-1">
                  {evals.recentTakeaways.map((t, i) => (
                    <div key={i} className="text-xs text-gray-five">
                      <span className="text-gray-bold">
                        {t.date} · {t.course} —{' '}
                      </span>
                      “{t.text}”
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Panel>

          <Panel title="Contacts" count={a.contactCount}>
            {a.contacts.length === 0 ? (
              <Empty />
            ) : (
              <div className="grid gap-1 text-sm">
                {a.contacts.map((c) => (
                  <Link
                    key={c.id}
                    to={PATH.opsContactEdit.replace(':id', c.id)}
                    className="transition-colors hover:text-blue-primary"
                  >
                    <span className="font-medium text-gray-four">{c.name}</span>
                    <span className="ml-2 text-xs text-gray-bold">
                      {[c.title, c.email].filter(Boolean).join(' · ')}
                    </span>
                  </Link>
                ))}
                {a.contactCount > a.contacts.length && (
                  <Link
                    to={`${PATH.opsContacts}?accountId=${a.id}`}
                    className="text-xs text-blue-primary hover:underline"
                  >
                    …and {a.contactCount - a.contacts.length} more — all{' '}
                    {a.contactCount} contacts
                  </Link>
                )}
              </div>
            )}
          </Panel>

          <div className="lg:col-span-2">
            <AttachmentsPanel recordId={a.id} recordObject="sf_account" />
          </div>

          <div className="lg:col-span-2">
            <CommentThread recordId={a.id} recordObject="account" />
          </div>
        </div>
      </PageBody>
    </>
  );
};

export default OpsAccountView;
